import { get } from 'lodash'

export const validColorNames = [
  'primary',
  'secondary',
  'dark',
  'medium',
  'light',
  'accent',
  'good',
  'warning',
  'attention',
  'link'
]
export const validFontSizeNames = ['small', 'default', 'medium', 'large', 'extraLarge']

/**
 * Get the color based on the theme, rootContainer and color name.
 * @param theme {object} - The theme object
 * @param rootContainer [string] - The container style name
 * @param colorName [string] - The color name for this container
 * @param contrastTo [string] - The level of color to pick "root" or "container"
 * @return {string} - resulting color
 */
export function getColor(theme: any, rootContainer = 'default', colorName = 'default', contrastTo = 'root') {
  const actualContrastTo = 'contrastTo' + contrastTo.charAt(0).toUpperCase() + contrastTo.slice(1).toLowerCase()
  // If the colorName is backgroundColor (or just background) then this will be a color
  let resultColor = get(
    theme,
    `rootContainers.${rootContainer}.${colorName}`,
    get(theme, `rootContainers.${rootContainer}.${colorName}Color`)
  )
  // Check the container colors, if an invalid color is given fallback to default contrastToRoot
  if (!resultColor) {
    resultColor = get(
      theme,
      `rootContainers.${rootContainer}.containers.${colorName}.${actualContrastTo}`,
      get(theme, `rootContainers.default.foregroundColors.default.contrastToRoot`)
    )
  }

  return resultColor
}

/**
 * Converts #AARRGGBB to rgba(rrr, ggg, bbb, a)
 * @param theme
 * @param {string} color - either #AARRGGBB, theme color name, or theme element name
 * @param {boolean} isSubtle - The caliber of the color
 * @returns {*}
 */
export function getRgbaColor(theme: any, color: any, isSubtle = false) {
  // See if the color is actually a theme component name (!in the valid list of color names)
  const argbReg = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})?/gi
  const rgbReg = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})?/gi
  const rgbMatch = rgbReg.exec(color)
  const argbMatch = argbReg.exec(color)
  if (argbMatch && argbMatch[4]) {
    const a = Number.parseInt(argbMatch[1], 16) / 255
    const r = Number.parseInt(argbMatch[2], 16)
    const g = Number.parseInt(argbMatch[3], 16)
    const b = Number.parseInt(argbMatch[4], 16)
    return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')'
  } else if (rgbMatch && rgbMatch[3]) {
    // @ts-expect-error
    const r = Number.parseInt(argbMatch[1], 16)
    // @ts-expect-error
    const g = Number.parseInt(argbMatch[2], 16)
    // @ts-expect-error
    const b = Number.parseInt(argbMatch[3], 16)
    return 'rgba(' + r + ',' + g + ',' + b + ',1)'
  } else if (validColorNames.includes(color)) {
    const caliber = isSubtle ? 'subtle' : 'normal'
    const requestedColorByName = get(theme, `colors.${color}.${caliber}`)
    if (requestedColorByName) {
      // Call ourselves because the theme has #AARRGGBB
      return getRgbaColor(theme, requestedColorByName)
    }
  } else if (color && Object.keys(theme).includes(color.match(/^\w*/)[0])) {
    // If the color is actually an element name then use that!
    // The element name may be specific (separator.borderColor) or point to a color compliant element (input)
    const themeComponent = get(theme, color)
    if (typeof themeComponent === 'object') {
      // The object we check to see if it has a color attribute and use that
      const colorValue = themeComponent.color || 'dark'
      return getRgbaColor(theme, colorValue, themeComponent.isSubtle)
    } else {
      // The element we are pointing to is an actual color, not a color compliant element
      return getRgbaColor(theme, get(theme, color), isSubtle)
    }
  } else if (color) {
    // If the color is at all defined, just use it (todo validate that it's an actuall css color)
    return color
  }
  // Yes there is no else, in the case we don't know what it is just let it inherit from the parent
}

/**
 * @deprecated
 * Convert #AARRGGBB to #RRGGBBAA, used mostly for native mobile clients
 */
export function getArgbColor(theme: any, color: any, isSubtle = false) {
  // See if the color is actually a theme component name (!in the valid list of color names)
  const argbReg = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})?/gi
  const rgbReg = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})?/gi
  const rgbMatch = rgbReg.exec(color)
  const argbMatch = argbReg.exec(color)
  if (argbMatch && argbMatch[4]) {
    const alpha = color.slice(1, 3)
    const rgb = color.slice(3)
    return `#${rgb}${alpha}`
  } else if (rgbMatch && rgbMatch[3]) {
    return `#${color.slice(1)}FF`
  } else if (validColorNames.includes(color)) {
    const caliber = isSubtle ? 'subtle' : 'normal'
    const requestedColorByName = get(theme, `colors.${color}.${caliber}`)
    if (requestedColorByName) {
      // Call ourselves because the theme has #AARRGGBB
      return getArgbColor(theme, requestedColorByName)
    }
  } else if (color && Object.keys(theme).includes(color.match(/^\w*/)[0])) {
    // If the color is actually an element name then use that!
    // The element name may be specific (separator.borderColor) or point to a color compliant element (input)
    const themeComponent = get(theme, color)
    if (typeof themeComponent === 'object') {
      // The object we check to see if it has a color attribute and use that
      const colorValue = themeComponent.color || 'dark'
      return getArgbColor(theme, colorValue, themeComponent.isSubtle)
    } else {
      // The element we are pointing to is an actual color, not a color compliant element
      return getArgbColor(theme, get(theme, color), isSubtle)
    }
  }
}

/**
 * Grab the font weight based on either a number or string in the theme file
 * @param theme
 * @param weightProperty
 * @returns {*}
 */
export function getFontWeight(theme: any, weightProperty: any) {
  const weightValue = get(theme, weightProperty, 'fontWeights.normal')
  if (typeof weightValue === 'number') {
    return weightValue
  }

  return get(theme, `fontWeights.${weightValue}`, theme.fontWeights.normal)
}

/**
 * @param theme
 * @param {string} fontSizeProperty - Either a specific size or a path to the component in theme
 * @returns {*}
 */
export function getFontSize(theme: any, fontSizeProperty: any) {
  if (typeof fontSizeProperty === 'number') {
    return fontSizeProperty
  } else if (validFontSizeNames.includes(fontSizeProperty)) {
    return get(theme, `fontSizes.${fontSizeProperty}`)
  } else if (Object.keys(theme).includes(fontSizeProperty)) {
    return getFontSize(theme, get(theme, `${fontSizeProperty}.size`))
  }
  // Inherit from parent in all other cases
}

/**
 * The go forward way and easy to get the font family, size, corresponding line height and weight of a font
 * @param {object} theme - The theme
 * @param {object} [options] - Options for the font
 * @param {string} [options.size] - The size of the font
 * @param {string} [options.weight] - The font weight
 * @param {boolean} [options.asObject = false] - Return as a string or object (for style instead of CSS)
 * @return {string}
 */
export function getFontDefinition(theme: any, options?: any) {
  const defaultOptions = { size: 'default', weight: 'normal', asObject: false }
  const opts = Object.assign({}, defaultOptions, options)
  if (opts.asObject) {
    return {
      fontFamily: get(theme, 'fontFamily'),
      fontSize: getFontSize(theme, opts.size),
      lineHeight: get(theme, `fontLineHeight.${opts.size}`, get(theme, 'fontLineHeight.default')),
      fontWeight: getFontWeight(theme, `fontWeights.${opts.weight}`).toString()
    }
  } else {
    return `
    font-family: ${get(theme, 'fontFamily')};
    font-size: ${getFontSize(theme, opts.size)}px;
    line-height: ${get(theme, `fontLineHeight.${opts.size}`, get(theme, 'fontLineHeight.default'))};
    font-weight: ${getFontWeight(theme, `fontWeights.${opts.weight}`)};
  `
  }
}

/**
 * @deprecated Font shadows are handled by the shared components
 * Get web shadow, this will return the css used in `drop-shadow` or `box-shadow`
 * @param {object} theme
 * @param {string} shadowDefinition location: EX: `newBubble.shadow`
 * @return string - 'offsetXpx offsetYpx blurpx rgbaColor' or 'none'
 */
export function getWebShadow(theme: any, shadowDefinition: any) {
  const definition = get(theme, shadowDefinition)
  return definition
    ? `${definition.offsetX}px ${definition.offsetY}px ${definition.blur}px ${getRgbaColor(theme, definition.color)}`
    : 'none'
}

/**
 * Get the react-native shadow definition based on the device and other oddities
 * You will get a style object
 * @param {object} theme
 * @param {string} [rootContainer] - The root container
 * @param {object} [options] - The blur and offset options
 * @param {number} [options.opacity] - The opacity
 * @return object
 */
export function getNativeShadow(theme: any, rootContainer = 'default', options = {}) {
  const resultOptions = Object.assign(
    {
      color: 'medium',
      opacity: 0.6,
      blur: 8,
      spread: 0,
      offsetX: 0,
      offsetY: 0
    },
    options
  )

  return {
    shadowRadius: resultOptions.blur,
    // @ts-expect-error
    shadowColor: getColor(theme, rootContainer, options.color),
    shadowOpacity: resultOptions.opacity,
    shadowOffset: { width: resultOptions.offsetX, height: resultOptions.offsetY },
    // For android, we don't really have a way to figure the elevation so it's more of a true/false
    elevation: 4
  }
}
